import React, { useState } from 'react';
import { BookOpen, ExternalLink, X, Download, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { literaryWorksData } from '../data/mock';
import { BookTheShowButton } from './BookTheShowButton';
import { ShareButton } from './ShareButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map of books that have PDFs available
// preview: true means show only preview (for purchase protection)
const bookPDFs = {
  'Meth Chronicles: The New Ice Age': {
    url: `${API_URL}/api/uploads/files/meth_chronicles_vol1-3.pdf`,
    preview: true,
    previewPages: 10,
    purchaseUrl: '#donate'
  },
  'A Moment With God': {
    url: `${API_URL}/api/uploads/files/a_moment_with_god.pdf`,
    preview: false,
    purchaseUrl: '#donate'
  },
  'Emotions in Motion': {
    url: `${API_URL}/api/uploads/files/emotions_in_motion.pdf`,
    preview: false,
    purchaseUrl: '#donate'
  }
};

export const LiteraryWorks = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedBook, setSelectedBook] = useState(null);
  const [showReader, setShowReader] = useState(false);
  
  const categories = ['All', 'Gritty Reality', 'Spiritual Journey', 'Inspirational', 'Philosophy', 'Poetry & Lyrics'];
  
  const filteredWorks = selectedCategory === 'All' 
    ? literaryWorksData 
    : literaryWorksData.filter(work => work.category === selectedCategory);

  const openReader = (book) => {
    setSelectedBook(book);
    setShowReader(true);
  };

  const closeReader = () => {
    setShowReader(false);
    setSelectedBook(null);
  };

  const hasPDF = (bookTitle) => !!bookPDFs[bookTitle];
  
  const getPDFUrl = (bookTitle) => {
    const pdf = bookPDFs[bookTitle];
    return typeof pdf === 'string' ? pdf : pdf?.url;
  };
  
  const isPreviewOnly = (bookTitle) => {
    const pdf = bookPDFs[bookTitle];
    return typeof pdf === 'object' && pdf?.preview;
  };

  return (
    <>
      {/* PDF Reader Modal */}
      {showReader && selectedBook && bookPDFs[selectedBook.title] && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-gunmetal-900 border-b border-steel-700/50">
            <div className="flex items-center space-x-4">
              <div>
                <h3 className="text-xl font-bold text-white">{selectedBook.title}</h3>
                <p className="text-steel-400 text-sm">{selectedBook.category}</p>
              </div>
              {isPreviewOnly(selectedBook.title) && (
                <span className="px-3 py-1 bg-yellow-500/20 border border-yellow-500/50 rounded-full text-yellow-400 text-xs font-bold uppercase">
                  Preview - First 10 Pages
                </span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {isPreviewOnly(selectedBook.title) ? (
                <a
                  href="#donate"
                  onClick={closeReader}
                  className="flex items-center space-x-2 px-4 py-2 bg-accent-red hover:bg-red-600 text-white rounded-lg transition-colors font-bold"
                >
                  <span>Purchase Full Book</span>
                </a>
              ) : (
                <>
                  <a
                    href={getPDFUrl(selectedBook.title)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                  >
                    <ExternalLink size={18} />
                    <span>Open in New Tab</span>
                  </a>
                  <a
                    href={getPDFUrl(selectedBook.title)}
                    download
                    className="flex items-center space-x-2 px-4 py-2 bg-gunmetal-700 hover:bg-gunmetal-600 text-white rounded-lg transition-colors"
                  >
                    <Download size={18} />
                    <span>Download</span>
                  </a>
                </>
              )}
              <button
                onClick={closeReader}
                className="w-10 h-10 bg-gunmetal-700 hover:bg-accent-red rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
          </div>
          
          {/* PDF Viewer - Using object tag with fallback */}
          <div className="flex-1 overflow-hidden bg-gray-800 flex flex-col">
            {isPreviewOnly(selectedBook.title) && (
              <div className="bg-gradient-to-r from-yellow-900/50 to-orange-900/50 px-4 py-3 text-center border-b border-yellow-500/30">
                <p className="text-yellow-300 font-semibold">
                  📖 Preview Mode - Showing first 10 pages. <a href="#donate" onClick={closeReader} className="underline hover:text-white">Purchase the full book</a> to read all 217 pages!
                </p>
              </div>
            )}
            <div className="flex-1 flex items-center justify-center">
              <object
                data={`${getPDFUrl(selectedBook.title)}#page=1`}
                type="application/pdf"
                className="w-full h-full"
              >
                {/* Fallback for browsers that don't support object PDF */}
                <div className="text-center p-8">
                  <BookOpen size={64} className="mx-auto mb-4 text-steel-400" />
                  <p className="text-white text-xl mb-4">PDF viewer not supported in your browser</p>
                  <div className="flex justify-center space-x-4">
                    <a
                      href={getPDFUrl(selectedBook.title)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                    >
                      Open PDF in New Tab
                    </a>
                    {!isPreviewOnly(selectedBook.title) && (
                      <a
                        href={getPDFUrl(selectedBook.title)}
                        download
                        className="px-6 py-3 bg-accent-red hover:bg-red-600 text-white rounded-lg transition-colors"
                      >
                        Download PDF
                      </a>
                    )}
                  </div>
                </div>
              </object>
            </div>
          </div>
        </div>
      )}

      <section id="books" className="relative py-24 bg-gunmetal-900">
      {/* Background Image */}
      <div className="absolute inset-0 opacity-10">
        <img 
          src="https://images.pexels.com/photos/6387583/pexels-photo-6387583.jpeg"
          alt="Books Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gunmetal-900 via-gunmetal-900/95 to-gunmetal-900"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4">
            <span className="text-platinum animate-chrome-pulse">LITERARY </span>
            <span className="text-accent-red">WORKS</span>
          </h2>
          <div className="w-24 h-1 bg-accent-red mx-auto mb-6"></div>
          <p className="text-xl text-steel-400 max-w-3xl mx-auto">
            30+ original stories spanning TV series, movies, books, and comics. From gritty realism to spiritual awakening.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-accent-red text-white shadow-lg shadow-accent-red/30'
                  : 'bg-gunmetal-800 text-steel-300 hover:bg-gunmetal-700 border border-steel-700/50'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Books Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {filteredWorks.map((work, index) => (
            <div 
              key={work.id}
              className="group bg-gunmetal-800/50 backdrop-blur-sm rounded-lg overflow-hidden border border-steel-700/30 hover:border-accent-red/50 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-accent-red/20"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Book Cover */}
              <div className="relative h-64 bg-gradient-to-br from-gunmetal-700 to-gunmetal-900 flex items-center justify-center overflow-hidden">
                {work.coverImage ? (
                  <img 
                    src={work.coverImage}
                    alt={work.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                ) : (
                  <>
                    <div className="absolute inset-0 opacity-20">
                      <img 
                        src="https://images.pexels.com/photos/34438970/pexels-photo-34438970.jpeg"
                        alt={work.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <BookOpen className="text-accent-red opacity-30 group-hover:opacity-60 transition-opacity duration-300" size={80} />
                  </>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-gunmetal-950 via-transparent to-transparent opacity-60"></div>
                <div className="absolute top-4 right-4">
                  <Badge className="bg-accent-red/90 text-white border-0">
                    {work.category}
                  </Badge>
                </div>
                {work.isNew && (
                  <div className="absolute top-4 left-4" data-testid={`new-badge-${work.id}`}>
                    <span className="inline-flex items-center gap-1 text-[10px] font-black tracking-[0.2em] px-2.5 py-1 bg-amber-400 text-gunmetal-950 rounded-sm shadow-lg shadow-amber-400/30 animate-pulse">
                      <span className="w-1.5 h-1.5 bg-gunmetal-950 rounded-full"></span>
                      NEW
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-steel-100 mb-3 group-hover:text-accent-red transition-colors duration-300">
                  {work.title}
                </h3>
                <p className="text-steel-400 mb-4 leading-relaxed">
                  {work.description}
                </p>

                {/* Formats */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {work.format.map((format, idx) => (
                    <span 
                      key={idx}
                      className="text-xs px-3 py-1 bg-gunmetal-700 text-steel-300 rounded-full border border-steel-600/30"
                    >
                      {format}
                    </span>
                  ))}
                </div>

                {/* Button */}
                {work.isBookable ? (
                  <BookTheShowButton showTitle={work.title} />
                ) : hasPDF(work.title) ? (
                  <div className="flex space-x-2">
                    <Button 
                      className="flex-1 bg-accent-red text-white hover:bg-red-600 transition-all duration-300 group/btn"
                      onClick={() => openReader(work)}
                      data-testid={`read-book-${work.id}`}
                    >
                      <Eye className="mr-2" size={16} />
                      Read Now
                    </Button>
                    <a
                      href={getPDFUrl(work.title)}
                      download
                      className="px-4 flex items-center justify-center bg-gunmetal-700 hover:bg-gunmetal-600 text-white rounded-md transition-colors"
                      data-testid={`download-book-${work.id}`}
                    >
                      <Download size={18} />
                    </a>
                  </div>
                ) : (
                  <Button 
                    className="w-full bg-transparent border border-accent-red text-accent-red hover:bg-accent-red hover:text-white transition-all duration-300 group/btn"
                    onClick={() => alert('PDF coming soon!')}
                  >
                    Coming Soon
                    <ExternalLink className="ml-2 group-hover/btn:translate-x-1 transition-transform duration-300" size={16} />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <p className="text-steel-400 mb-6">
            Explore our complete collection of transformative literature
          </p>
          <Button 
            className="bg-accent-red hover:bg-accent-red-dark text-white font-bold px-8 py-6 text-lg transition-all duration-300 hover:shadow-2xl hover:shadow-accent-red/40"
            onClick={() => alert('Full catalog coming soon!')}
          >
            View Complete Catalog
          </Button>
          <div className="mt-6">
            <ShareButton
              sectionId="books"
              sectionTitle="MINDESIGN Literary Works"
              description="30+ original stories — Velocity Lock, Echos of Resilience, Borrachero, Earthbound, and the entire MINDESIGN ENTERPRISES library."
            />
          </div>
        </div>
      </div>
    </section>
    </>
  );
};
